import { useEffect, useState } from "react";
import { FiArrowRightCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/common/pageheader/PageHeader";

import { createUser, deleteUser, getUserById, getUsers, updateUser } from "../../services/usersApi";

import ActionsMenu from "../../components/common/actionsMenu/ActionsMenu";
import ConfirmDeleteModal from "../../components/common/deleteModal/ConfirmDeleteModal";
import UserFormModal from "./UserFormModal/UserFormModal";
import TableEmptyState from "../../components/common/tableEmptyState/TableEmptyState";
import Select from "../../components/common/select/Select";
import styles from "./UsersPage.module.css";
import Loader from "../../components/common/Loader/Loader";

const PHONE_LOCAL_REGEX = /^[1-9]\d{8}$/;
const stripPhonePrefix = (stored) => {
  if (!stored) return "";
  return stored.startsWith("+994") ? stored.slice(4) : stored;
};

const DEFAULT_FORM = {
  name: "",
  email: "",
  role: "ADMIN",
  nationalId: "",
  phone: "",
  address: "",
};

const UsersPage = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchName, setSearchName] = useState("");
  const [searchEmail, setSearchEmail] = useState("");
  const [searchRole, setSearchRole] = useState("");
  const [appliedSearch, setAppliedSearch] = useState({ name: "", email: "", role: "" });

  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const navigate = useNavigate();

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString("az-Latn-AZ");
    } catch {
      return "-";
    }
  };

  const fetchUsers = async (forcedPageNo) => {
    const p = typeof forcedPageNo === "number" ? forcedPageNo : pageNo;

    try {
      setLoading(true);
      setError("");

      const filters = {
        name: searchName,
        email: searchEmail,
        role: searchRole,
        pageNo: p,
        pageSize,
      };

      setAppliedSearch({ name: searchName || "", email: searchEmail || "", role: searchRole || "" });

      const data = await getUsers(filters);

      const list = Array.isArray(data?.content) ? data.content : [];
      setUsers(list);

      const total = typeof data?.totalPages === "number" ? data.totalPages : 1;
      setTotalPages(total > 0 ? total : 1);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.users.loadError");

      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pageNo]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setPageNo(1);
    fetchUsers(1);
  };

  const openCreateModal = () => {
    setFormMode("create");
    setFormValues(DEFAULT_FORM);
    setIsModalOpen(true);
  };

  const openEditModal = async (userId) => {
    try {
      setFormMode("edit");
      setSaving(false);
      setError("");

      const data = await getUserById(userId);

      setFormValues({
        name: data.name || "",
        email: data.email || "",
        role: data.role || "ADMIN",
        nationalId: data.nationalId || "",
        phone: stripPhonePrefix(data.phone),
        address: data.address || "",
        id: data.id,
      });

      setIsModalOpen(true);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.users.loadDetailError");

      setError(msg);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormValues(DEFAULT_FORM);
    setError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      if (!formValues.name.trim()) {
        setError(t("pages.users.nameRequired"));
        setSaving(false);
        return;
      }

      if (!formValues.email.trim()) {
        setError(t("pages.users.emailRequired"));
        setSaving(false);
        return;
      }

      const phoneLocal = (formValues.phone || "").trim();
      if (phoneLocal && !PHONE_LOCAL_REGEX.test(phoneLocal)) {
        setError(t("validation.phoneFormat"));
        setSaving(false);
        return;
      }

      const payload = {
        email: formValues.email.trim(),
        name: formValues.name.trim(),
        role: formValues.role,
        nationalId: formValues.nationalId?.trim() || "",
        phone: phoneLocal ? `+994${phoneLocal}` : null,
        address: formValues.address?.trim() || "",
      };

      if (formMode === "create") {
        await createUser(payload);
        toast.success(t("pages.users.createSuccess"), {
          toastId: "user-create-success",
        });
      } else {
        await updateUser(formValues.id, payload);
        toast.success(t("pages.users.updateSuccess"), {
          toastId: "user-update-success",
        });
      }

      closeModal();
      setPageNo(1);
      fetchUsers(1);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.users.operationFailed");

      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (user) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setLoading(true);
      setError("");

      await deleteUser(userToDelete.id);

      toast.success(t("pages.users.deleteSuccess"));

      setDeleteModalOpen(false);
      setUserToDelete(null);
      setPageNo(1);
      fetchUsers(1);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        t("pages.users.deleteFailed");

      setError(msg);
    } finally {
      setLoading(false);
      setDeleteModalOpen(false);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const goToPrevPage = () => {
    setPageNo((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNo((prev) => (prev < totalPages ? prev + 1 : prev));
  };

  return (
    <div className={styles.container}>
      <PageHeader title={t("pages.users.title")} />

      <form className={styles.filterForm} onSubmit={handleFilterSubmit}>
        <input
          type="text"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          className={styles.searchInput}
          placeholder={t("pages.users.searchByName")}
        />

        <input
          type="text"
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          className={styles.searchInput}
          placeholder={t("pages.users.searchByEmail")}
        />

        <Select
          value={searchRole}
          onChange={(e) => setSearchRole(e.target.value)}
          className={styles.searchInput}
          options={[
            { value: "", label: t("pages.users.allRoles") },
            { value: "OWNER", label: t("userRole.OWNER") },
            { value: "ADMIN", label: t("userRole.ADMIN") },
            { value: "COORDINATOR", label: t("userRole.COORDINATOR") },
            { value: "TEACHER", label: t("userRole.TEACHER") },
          ]}
        />

        <button type="submit" className={styles.primaryBtn}>
          {t("common.search")}
        </button>

        <button type="button" onClick={openCreateModal} className={styles.secondaryBtn}>
          {t("pages.users.addUser")}
        </button>
      </form>

      {loading && <Loader fullscreen />}
      {error && <p className={styles.error}>{t("common.errorLabel")}: {error}</p>}

      {!loading && users.length === 0 && !error && (
        <TableEmptyState
          title={t("pages.users.noUsersFound")}
          description={appliedSearch.name || appliedSearch.email || appliedSearch.role ? t("pages.users.changeCriteria") : t("pages.users.addNew")}
          icon={appliedSearch.name || appliedSearch.email || appliedSearch.role ? "search" : "inbox"}
        />
      )}

      {!loading && users.length > 0 && (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("pages.users.name")}</th>
                  <th>{t("pages.users.email")}</th>
                  <th>{t("pages.users.role")}</th>
                  <th>{t("pages.users.createdDate")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td>{u.email}</td>
                    <td>{u.role ? t(`userRole.${u.role}`, { defaultValue: u.role }) : "-"}</td>
                    <td>{formatDateTime(u.createdDateTime)}</td>
                    <td className={styles.actions}>
                      <button type="button" onClick={() => navigate(`/users/${u.id}`)} className={styles.iconBtn}>
                        <FiArrowRightCircle size={18} />
                      </button>

                      <ActionsMenu onEdit={() => openEditModal(u.id)} onDelete={() => openDeleteModal(u)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            <button type="button" onClick={goToPrevPage} disabled={pageNo === 1}>
              {t("common.previous")}
            </button>

            <span>
              {t("common.pageOf", { page: pageNo, total: totalPages || 1 })}
            </span>

            <button type="button" onClick={goToNextPage} disabled={pageNo >= totalPages}>
              {t("common.next2")}
            </button>
          </div>
        </>
      )}

      <UserFormModal
        isOpen={isModalOpen}
        mode={formMode}
        values={formValues}
        onChange={handleFormChange}
        onClose={closeModal}
        onSubmit={handleFormSubmit}
        saving={saving}
        error={error}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onConfirm={confirmDeleteUser}
        onCancel={cancelDeleteUser}
        message={userToDelete ? t("pages.users.deleteConfirm", { name: userToDelete.name }) : ""}
        disabled={loading}
      />
    </div>
  );
};

export default UsersPage;
